/**
 * Twist bench (?demo=twist) — a static A/B rig for the reorient chute:
 * four kit straights, the chute over eight cells, four more kit
 * straights presented under the exit frame, connected FLUSH at both
 * ends. Keys 1/2/3/4 set the roll (0° / +90° / −90° / 180°); 0° must
 * read as one unbroken kit channel (the chute IS the sliced kit
 * section), the rolls as that same channel bending.
 */
import * as THREE from "three";
import type { Assets } from "./assets.ts";
import type { SceneCtx } from "./scene.ts";
import { createCameras } from "./camera.ts";
import { fitShadowsTo } from "./scene.ts";
import { AFFINE_IDENTITY, affineApply, composeTwist } from "../shared/frame.ts";
import { TRACK_SCALE } from "../gen/endless/scale.ts";
import { buildChuteMesh, extractChannelSection } from "./twistChute.ts";

const CELLS_IN = 4;
const CELLS_TWIST = 8;
const CELLS_OUT = 4;
const STEP = 0.5;

export function buildTwistDemo(ctx: SceneCtx, assets: Assets): void {
  const section = extractChannelSection(assets);
  if (!section) {
    console.warn("[twist-demo] no straight piece — cannot build");
    return;
  }
  const S = TRACK_SCALE;
  const group = new THREE.Group();
  group.name = "twist-demo";
  ctx.scene.add(group);

  let angle = Math.PI / 2;
  const rebuild = () => {
    group.clear();
    // Entry straights: cells −4..−1 (channel along +z, stock rotation).
    for (let k = -CELLS_IN; k < 0; k++) {
      const obj = assets.getPiece("straight");
      if (!obj) continue;
      obj.position.set(0, 0, k * S);
      obj.scale.setScalar(S);
      group.add(obj);
    }
    // The twist spans cell edges: from the entry run's last edge to the
    // exit run's first, pivoting on the channel-floor centerline.
    const zA = -S / 2;
    const len = CELLS_TWIST * S;
    const pivot: [number, number, number] = [0, section.floorY * S, zA];
    const count = Math.round(len / STEP) + 1;
    const scratch: [number, number, number] = [0, 0, 0];
    const chute = buildChuteMesh(section, count, (i, x, y, out) => {
      const z = zA + i * STEP;
      const u = Math.min(1, Math.max(0, (z - zA) / len));
      const f = u <= 0 ? AFFINE_IDENTITY : composeTwist(
        AFFINE_IDENTITY,
        [0, 0, 1],
        angle * u,
        pivot,
      );
      scratch[0] = x * S;
      scratch[1] = y * S;
      scratch[2] = z;
      affineApply(f, scratch, scratch);
      out.set(scratch[0], scratch[1], scratch[2]);
    });
    group.add(chute);
    // Exit straights: cells +8.. under the full exit frame.
    const exit = composeTwist(AFFINE_IDENTITY, [0, 0, 1], angle, pivot);
    const post = new THREE.Group();
    post.quaternion.set(exit.q[0], exit.q[1], exit.q[2], exit.q[3]);
    post.position.set(exit.t[0], exit.t[1], exit.t[2]);
    for (let k = CELLS_TWIST; k < CELLS_TWIST + CELLS_OUT; k++) {
      const obj = assets.getPiece("straight");
      if (!obj) continue;
      obj.position.set(0, 0, k * S);
      obj.scale.setScalar(S);
      post.add(obj);
    }
    group.add(post);
  };
  rebuild();

  const box = new THREE.Box3().setFromObject(group);
  fitShadowsTo(ctx.sun, box);
  ctx.scene.fog = null;
  const cams = createCameras(ctx, null, null);
  cams.frame(box);
  ctx.onFrame.add((dt) => cams.update(dt));

  addEventListener("keydown", (ev) => {
    const pick: Record<string, number> = {
      "1": 0,
      "2": Math.PI / 2,
      "3": -Math.PI / 2,
      "4": Math.PI,
    };
    if (ev.key in pick) {
      angle = pick[ev.key];
      rebuild();
    }
  });
}
